import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import { usePopup } from '../context/PopupContext';
import { deriveStatus, statusMeta, canEdit, canDelete } from '../utils/testStatus';
import './TestsPro.css';

const Tests = () => {
  const navigate = useNavigate();
  const { showConfirm, showAlert } = usePopup();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchTests = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get('/tests');
      const enrichedTests = res.data.map(t => ({
        ...t,
        derived_status: deriveStatus(t)
      }));
      setTests(enrichedTests);
    } catch (err) { 
      console.error(err);
      setError('Failed to load tests.');
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { fetchTests(); }, []);

  const handleDelete = async (test) => {
    if (!canDelete(test)) return;
    const confirmed = await showConfirm('Delete Test', 'Delete this test? All assignments and results will be lost.');
    if (!confirmed) return;
    try {
      await API.delete(`/tests/${test.id}`);
      fetchTests();
      showAlert('Success', 'Test deleted successfully');
    } catch { 
      showAlert('Error', 'Failed to delete test'); 
    }
  };

  const handlePublish = async (test) => {
    const confirmed = await showConfirm('Publish Test', 'Are you sure you want to publish this test? It will become active immediately.');
    if (!confirmed) return;
    try {
      await API.post(`/tests/${test.id}/publish`);
      fetchTests();
      showAlert('Success', 'Test published successfully');
    } catch {
      showAlert('Error', 'Failed to publish test');
    }
  };

  // Stats
  const stats = {
    total: tests.length,
    draft: tests.filter(t => t.derived_status === 'draft').length,
    scheduled: tests.filter(t => t.derived_status === 'scheduled').length,
    active: tests.filter(t => t.derived_status === 'active').length,
    completed: tests.filter(t => t.derived_status === 'completed').length,
  };

  // Filtering
  const filteredTests = tests.filter(t => {
    const matchesSearch = t.test_name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'All' || t.derived_status.toLowerCase() === statusFilter.toLowerCase();
    const matchesType = typeFilter === 'All' || (t.test_type || '').toLowerCase() === typeFilter.toLowerCase();
    return matchesSearch && matchesStatus && matchesType;
  });

  // Sorting
  const sortedTests = [...filteredTests].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    if (sortConfig.key === 'created_at') {
      aVal = new Date(a.created_at || 0).getTime();
      bVal = new Date(b.created_at || 0).getTime();
    }
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedTests.length / itemsPerPage);
  const paginatedTests = sortedTests.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  return (
    <div className="tmx-page">
      <div className="tmx-top">
        <div>
          <h1>Test Management</h1>
          <p>Create, manage, and analyze all your assessments.</p>
        </div>
        <div className="tmx-actions">
          <button className="tmx-btn primary" onClick={() => navigate('/admin/tests/create')} aria-label="Create New Test">
            Create New Test
          </button>
        </div>
      </div>

      <div className="tmx-stats">
        <div className="tmx-stat"><div className="v">{stats.total}</div><div className="l">Total Tests</div></div>
        <div className="tmx-stat"><div className="v">{stats.draft}</div><div className="l">Drafts</div></div>
        <div className="tmx-stat"><div className="v">{stats.scheduled}</div><div className="l">Scheduled</div></div>
        <div className="tmx-stat"><div className="v">{stats.active}</div><div className="l">Active</div></div>
        <div className="tmx-stat"><div className="v">{stats.completed}</div><div className="l">Completed</div></div>
      </div>

      <div className="tmx-toolbar">
        <input 
          type="text" 
          placeholder="Search tests by name..." 
          value={search} 
          onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} 
          className="tmx-search" 
        />
        <select className="tmx-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
          <option value="All">Status: All</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
        <select className="tmx-select" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setCurrentPage(1); }}>
          <option value="All">Type: All</option>
          <option value="Aptitude Test">Aptitude</option>
          <option value="Diagnostic Test">Diagnostic</option>
        </select>
        {(search || statusFilter !== 'All' || typeFilter !== 'All') && (
          <button className="tmx-clear" onClick={() => { setSearch(''); setStatusFilter('All'); setTypeFilter('All'); }}>
            Clear Filters
          </button>
        )}
      </div>

      {error && (
        <div className="tmx-state" style={{color: '#dc2626'}}>
          <p>{error}</p>
          <button className="tmx-btn" onClick={fetchTests}>Retry</button>
        </div>
      )}

      {!error && (
        <div className="tmx-table-wrap">
          <table className="tmx-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => requestSort('test_name')}>
                  Test Name {sortConfig.key === 'test_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable" onClick={() => requestSort('test_type')}>
                  Type {sortConfig.key === 'test_type' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable" onClick={() => requestSort('question_count')}>
                  Questions {sortConfig.key === 'question_count' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Progress</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i}>
                    <td colSpan="6"><div className="tmx-skel"></div></td>
                  </tr>
                ))
              ) : paginatedTests.length === 0 ? (
                <tr><td colSpan="6" className="tmx-state">No tests found matching your criteria.</td></tr>
              ) : (
                paginatedTests.map(t => {
                  const sMeta = statusMeta(t.derived_status);
                  const editable = canEdit(t);
                  const deletable = canDelete(t);

                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="tmx-name" onClick={() => navigate(`/admin/tests/${t.id}`)}>{t.test_name}</div>
                        <div style={{fontSize: '0.75rem', color: '#64748b', marginTop: '2px'}}>
                          Created {new Date(t.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td><span className="tmx-pill" style={{background: '#f1f5f9', color: '#475569'}}>{t.test_type || 'General'}</span></td>
                      <td>{t.question_count || 0}</td>
                      <td>
                        <div style={{fontSize: '0.8rem', color: '#334155'}}>
                          {t.completed_count || 0} / {t.assigned_count || 0}
                        </div>
                      </td>
                      <td>
                        <span className="tmx-badge" style={{ background: sMeta.bg, color: sMeta.color }}>
                          {sMeta.label}
                        </span>
                      </td>
                      <td className="tmx-actions tmx-row-actions">
                        <button 
                          className="tmx-icon-btn" 
                          onClick={() => navigate(`/admin/tests/${t.id}?tab=results`)} 
                          title="View Results" 
                          aria-label="View Results"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
                        </button>
                        
                        {t.derived_status === 'draft' && (
                           <button 
                            className="tmx-icon-btn" 
                            onClick={() => handlePublish(t)} 
                            title="Publish Test" 
                            aria-label="Publish Test"
                           >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                           </button>
                        )}
                        
                        <button 
                          className="tmx-icon-btn" 
                          onClick={() => navigate(`/admin/tests/${t.id}`)} 
                          title="Manage" 
                          aria-label="Manage Test"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                        </button>
                        
                        <button 
                          className="tmx-icon-btn" 
                          onClick={() => navigate(`/admin/tests/edit/${t.id}`)} 
                          title={editable ? "Edit" : "Cannot edit test with submitted attempts"} 
                          aria-label="Edit Test"
                          disabled={!editable}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                        </button>
                        
                        <button 
                          className="tmx-icon-btn del" 
                          onClick={() => handleDelete(t)} 
                          title={deletable ? "Delete" : "Cannot delete test with submitted attempts"} 
                          aria-label="Delete Test"
                          disabled={!deletable}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          
          {totalPages > 1 && (
            <div className="tmx-pagination">
              <span>Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedTests.length)} of {sortedTests.length} tests</span>
              <div className="tmx-page-btns">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Tests;